/**
 * The units of measure SIFEN accepts, with the code that travels in the XML.
 *
 * The same table the XML generator validates against
 * (backend/src/facturacionelectronicapy-xmlgen/services/constants.service.ts),
 * so a unit picked here is never rejected there. The code is what gets stored
 * and sent; the name is what a person reads.
 */
export interface MeasureUnit {
  code: number;
  symbol: string;
  description: string;
}

/**
 * Unit first, because it is what almost every line uses and what the panel has
 * always sent; the rest in alphabetical order.
 */
export const MEASURE_UNITS: MeasureUnit[] = [
  { code: 77, symbol: "UNI", description: "Unidad" },
  { code: 97, symbol: "AA", description: "Año" },
  { code: 91, symbol: "CM", description: "Centímetros" },
  { code: 92, symbol: "CM2", description: "Centímetros cuadrados" },
  { code: 93, symbol: "CM3", description: "Centímetros cúbicos" },
  { code: 2366, symbol: "CPM", description: "Costo por mil" },
  { code: 104, symbol: "DET", description: "Determinación" },
  { code: 102, symbol: "Di", description: "Día" },
  { code: 86, symbol: "g", description: "Gramos" },
  { code: 869, symbol: "ha", description: "Hectáreas" },
  { code: 100, symbol: "Hs", description: "Hora" },
  { code: 83, symbol: "kg", description: "Kilogramos" },
  { code: 79, symbol: "kg/m2", description: "Kilogramos por metro cuadrado" },
  { code: 625, symbol: "Km", description: "Kilómetros" },
  { code: 89, symbol: "LT", description: "Litros" },
  { code: 98, symbol: "ME", description: "Mes" },
  { code: 660, symbol: "ml", description: "Metro lineal" },
  { code: 87, symbol: "m", description: "Metros" },
  { code: 108, symbol: "MT", description: "Metros" },
  { code: 109, symbol: "M2", description: "Metros cuadrados" },
  { code: 110, symbol: "M3", description: "Metros cúbicos" },
  { code: 90, symbol: "MG", description: "Miligramos" },
  { code: 88, symbol: "ML", description: "Mililitros" },
  { code: 95, symbol: "MM", description: "Milímetros" },
  { code: 96, symbol: "MM2", description: "Milímetros cuadrados" },
  { code: 101, symbol: "Mi", description: "Minuto" },
  { code: 891, symbol: "pm", description: "Por milaje" },
  { code: 94, symbol: "PUL", description: "Pulgadas" },
  { code: 569, symbol: "ración", description: "Ración" },
  { code: 666, symbol: "Se", description: "Segundo" },
  { code: 99, symbol: "TN", description: "Tonelada" },
  { code: 2329, symbol: "UI", description: "Unidad internacional" },
  { code: 885, symbol: "GL", description: "Unidad de medida global" },
  { code: 103, symbol: "Ya", description: "Yardas" },
];

/**
 * A unit as a person reads it. The symbol goes along because two of the names
 * repeat (SIFEN has two codes for metres).
 */
export const measureUnitLabel = (unit: MeasureUnit): string =>
  `${unit.description} (${unit.symbol})`;

/**
 * The unit a stored code stands for. A code outside the table can still arrive
 * from a line created through the API, and it is shown as a code rather than
 * hidden.
 */
export const findMeasureUnit = (code: number): MeasureUnit =>
  MEASURE_UNITS.find((unit) => unit.code === code) ?? {
    code,
    symbol: String(code),
    description: "Código",
  };
